import { useMemo } from 'react';

import { Criterion, ChoiceChipV2, SxProps } from '@cast/design-system';
import { useSearchCriterion } from '../hooks/useSearchCriterion';

type OptionItem<T = string> = {
  title: React.ReactNode;
  value: T;
};

type Props<T = string> = {
  criterion?: Criterion | string;
  options: Array<OptionItem<T> | string>;
  analyticsHandler?: (value?: T | T[]) => void;
  multiple?: boolean;
  chipSx?: SxProps;
};

export const QuickSearchFilter = <T extends string>({
  criterion: _criterion,
  options,
  analyticsHandler,
  multiple,
  chipSx,
}: Props<T>) => {
  const criterion = useSearchCriterion(_criterion);
  const _options = options.map((opt) =>
    typeof opt === 'string' ? { title: opt, value: opt as T } : opt
  );

  const _currentValue = useMemo(() => {
    if (multiple) {
      if (!criterion?.value?.includes) {
        return [];
      }

      return _options
        .filter(({ value }) => criterion.value.includes(value))
        .map((v) => v.value);
    }

    return criterion?.value
      ? _options.find((opt) => opt.value === criterion.value)?.value
      : undefined;
  }, [_options, criterion.value, multiple]);

  const isChecked = (opt: OptionItem<T>) => {
    if (Array.isArray(_currentValue)) {
      return _currentValue.some((value) => value === opt.value);
    }

    return opt.value === _currentValue;
  };

  const handleOnSelect = (opt: OptionItem<T>) => {
    let newValue: T | T[] | undefined;
    const checked = isChecked(opt);

    if (checked) {
      // if multiple - remove from array, if singular - set to undefined
      newValue = Array.isArray(_currentValue)
        ? _currentValue.filter((value) => value !== opt.value)
        : undefined;
    } else {
      // if multiple - add to array, if singular - set to selected value
      newValue = Array.isArray(_currentValue)
        ? [..._currentValue, opt.value]
        : opt.value;
    }

    criterion?.setValue?.(newValue);
    analyticsHandler?.(newValue);
  };

  return (
    <>
      {_options.map((opt) => (
        <ChoiceChipV2
          key={String(opt.value)}
          selected={isChecked(opt)}
          onClick={() => {
            handleOnSelect(opt);
          }}
          sx={chipSx}
          label={opt.title}
        />
      ))}
    </>
  );
};
